import * as Dialog from "@radix-ui/react-dialog";
import { Search } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { clsx } from "clsx";
import { navGroups } from "../nav";

type Entry = { to: string; label: string; group: string };

const entries: Entry[] = navGroups.flatMap((g) =>
    g.items.map((item) => ({ to: item.to, label: item.label, group: g.title }))
);

export function DocsCommandPalette({
    open,
    onOpenChange,
}: {
    open: boolean;
    onOpenChange: (open: boolean) => void;
}) {
    const [q, setQ] = useState("");
    const [active, setActive] = useState(0);
    const navigate = useNavigate();

    const filtered = useMemo(() => {
        const query = q.trim().toLowerCase();
        if (!query) return entries;
        return entries.filter(
            (e) =>
                e.label.toLowerCase().includes(query) ||
                e.group.toLowerCase().includes(query) ||
                e.to.includes(query)
        );
    }, [q]);

    useEffect(() => {
        if (!open) setQ("");
        setActive(0);
    }, [open, q]);

    const go = (to: string) => {
        navigate(to);
        onOpenChange(false);
        setQ("");
    };

    return (
        <Dialog.Root open={open} onOpenChange={onOpenChange}>
            <Dialog.Portal>
                <Dialog.Overlay className="fixed inset-0 z-modal bg-black/55 backdrop-blur-[2px]" />
                <Dialog.Content
                    className="fixed left-1/2 top-[15%] z-modal w-[min(100%-1.5rem,560px)] -translate-x-1/2 overflow-hidden rounded-2xl border border-neutral-border bg-white shadow-mz-xl outline-none dark:border-white/12 dark:bg-neutral-navy"
                    onKeyDown={(e) => {
                        if (e.key === "ArrowDown") {
                            e.preventDefault();
                            setActive((i) => Math.min(i + 1, Math.max(filtered.length - 1, 0)));
                        } else if (e.key === "ArrowUp") {
                            e.preventDefault();
                            setActive((i) => Math.max(i - 1, 0));
                        } else if (e.key === "Enter" && filtered[active]) {
                            e.preventDefault();
                            go(filtered[active].to);
                        }
                    }}
                >
                    <Dialog.Title className="sr-only">Search design system</Dialog.Title>
                    <div className="flex items-center gap-2 border-b border-neutral-border px-3 dark:border-white/10">
                        <Search
                            size={18}
                            className="text-primary dark:text-gold-light"
                            aria-hidden
                        />
                        <input
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Search docs…"
                            className="h-12 w-full bg-transparent text-sm text-neutral-ink outline-none placeholder:text-neutral-secondary dark:text-white"
                            autoFocus
                        />
                        <kbd className="hidden rounded border border-neutral-border px-1.5 py-0.5 font-mono text-[10px] text-neutral-secondary sm:inline dark:border-white/15">
                            esc
                        </kbd>
                    </div>
                    <ul className="max-h-80 overflow-y-auto p-2" role="listbox">
                        {filtered.length === 0 ? (
                            <li className="px-3 py-6 text-center text-sm text-neutral-secondary">
                                No matches
                            </li>
                        ) : (
                            filtered.map((item, i) => (
                                <li key={item.to}>
                                    <button
                                        type="button"
                                        role="option"
                                        aria-selected={i === active}
                                        className={clsx(
                                            "flex w-full items-center justify-between rounded-xl px-3 py-2.5 text-start text-sm",
                                            i === active
                                                ? "bg-primary text-white dark:bg-gold dark:text-neutral-ink"
                                                : "text-neutral-dark hover:bg-neutral-light dark:text-slate-200 dark:hover:bg-white/5"
                                        )}
                                        onMouseEnter={() => setActive(i)}
                                        onClick={() => go(item.to)}
                                    >
                                        <span className="font-medium">{item.label}</span>
                                        <span
                                            className={clsx(
                                                "text-[10px] uppercase tracking-wide",
                                                i === active
                                                    ? "opacity-80"
                                                    : "text-neutral-secondary"
                                            )}
                                        >
                                            {item.group}
                                        </span>
                                    </button>
                                </li>
                            ))
                        )}
                    </ul>
                    <div className="border-t border-neutral-border px-3 py-2 text-[11px] text-neutral-secondary dark:border-white/10">
                        ↑↓ navigate · Enter open · Esc close
                    </div>
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    );
}
